import React from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import RoundedPaper from 'shared/components/RoundedPaper';
import { whenItRuns } from './parts';
import { AgentState } from './types';
import AlertRecipients from './AlertRecipients';
import RunSettings from './RunSettings';

// Most settings live in each agent's agent.yaml in the hub repo; a change goes through a PR the pipeline reviews.
// When it runs (RunSettings) and who its alerts go to (AlertRecipients) are set on this page instead, applied by the hub live.
const HUB_REPO = 'https://github.com/shareably/agent-hub';
const fileUrl = (name: string, file: string, edit = false) => `${HUB_REPO}/${edit ? 'edit' : 'blob'}/main/.claude/skills/${name}/${file}`;

function workdirLabel(w: string): string {
  if (w === 'pr') return "The PR's branch";
  if (w === 'scratch') return 'A scratch folder';
  return w;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{label}</Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

/** The essentials of how an agent is set up, and where to change it (its agent.yaml, through a PR). */
export default function AgentSettings({ agent }: { agent: AgentState }) {
  const { stats } = agent;
  const s = agent.settings;
  return (
    <RoundedPaper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, flexWrap: 'wrap' }}>
        <Fact label="Runs">
          {whenItRuns(agent)}
          {agent.status !== 'active' ? ` (${agent.status})` : ''}
        </Fact>
        {s && <Fact label="Works in">{workdirLabel(s.workdir)}</Fact>}
        <Fact label="Last 7 days">{stats?.runs7d ? `${stats.runs7d} runs, ~${stats.avgMinutes} min each` : 'No runs'}</Fact>
        {agent.liveActions.length > 0 && <Fact label="Live actions">{agent.liveActions.length}, each approved in Slack</Fact>}
        <Box sx={{ flex: 1 }} />
        <Tooltip arrow describeChild title="Opens its agent.yaml on GitHub; saving opens a pull request">
          <Button size="small" variant="outlined" href={fileUrl(agent.name, 'agent.yaml', true)} target="_blank" rel="noreferrer">Edit settings</Button>
        </Tooltip>
        <Button size="small" href={fileUrl(agent.name, 'SKILL.md')} target="_blank" rel="noreferrer">Instructions</Button>
      </Box>
      <RunSettings agent={agent} />
      <AlertRecipients agent={agent.name} />
    </RoundedPaper>
  );
}
